import api from './index'
import { Base64 } from 'js-base64'
import type { ApiResponse, V1ApiResponse } from '@/types'
import { APP_LOGIN_SMS_CODE } from '@/api/sms'

export const AUTH_TYPE_MOBILE = 20

export interface MobileLoginParams {
  authType?: number
  mobile: string
  code: string
  smsPushCode?: string
  invitationCode?: string
}

export interface MobileLoginUser {
  userId: number
  uuid: string
  mobile: string
  nickname: string
  shareCode: string
  invitationCode: string
}

export interface MobileLoginData {
  user: MobileLoginUser
  auth: {
    userAuthId: number
    authType: number
  }
  tokenType: string
  accessToken: string
  refreshToken: string
  expiresIn: number
  isNewUser: boolean
}

export interface RefreshTokenData {
  tokenType: string
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface UserDetailUser {
  userId: number
  uuid: string
  avatar?: string | null
  backgroundUrl?: string | null
  nickname: string
  sex: number
  birthday?: string | null
  countryCode: string
  country: string
  province?: string | null
  city?: string | null
  intro?: string | null
  shareCode: string
  invitationCode: string
  realAuthState: boolean
  realAuthDateTime?: string | null
  certificationId?: number | null
  authorized: boolean
  lastUserAuthId: number
  createDateTime: string
  updateDateTime: string
  deleted: boolean
  enabled: boolean
  remark?: string | null
}

export interface UserDetailStat {
  userId: number
  dynamicTotality: number
  followedQuantity: number
  fansQuantity: number
  likedQuantity: number
  aiBabyQuantity: number
  sameStyleQuantity: number
  footmarkTotality: number
  reportCount: number
  blockCount: number
  createDateTime: string
  updateDateTime: string
  deleted: boolean
  enabled: boolean
  remark?: string | null
}

export interface UserDetailData {
  isSelf: boolean
  user: UserDetailUser
  stat: UserDetailStat
  isFollow?: boolean
  followState?: string
  isBlocked?: boolean
}

export interface UpdateUserProfileParams {
  avatar?: string
  backgroundUrl?: string
  nickname?: string
  intro?: string
}

export interface UserProfileAuditItem {
  profileAuditId: number
  fieldCode: string
  mainAuditStatus: number
  subAuditStatus: number
  auditRemark?: string | null
  completedDateTime?: string
}

export interface UpdateUserProfileData {
  requestId?: string
  noChange: boolean
  audits: UserProfileAuditItem[]
}

/** 账户注销流程状态：1待执行 2执行中 3已注销 4用户已终止 5管理员已终止 6执行失败待重试 */
export const ACCOUNT_CANCELLATION_STATUS_PENDING = 1

export interface AccountCancellationData {
  cancellationId: number
  cancellationNo: string
  status: number
  statusName: string
  countdownLabel: string
  countdownSeconds: number
  submittedDateTime: string
  executeDateTime: string
  terminatedBy: number | null
  terminatedDateTime: string | null
  completedDateTime: string | null
  remainingSeconds: number
  canTerminate: boolean
}

export const USER_PROFILE_MAIN_AUDIT_PENDING = 1
export const USER_PROFILE_MAIN_AUDIT_REVIEWING = 2
export const USER_PROFILE_MAIN_AUDIT_SUCCESS = 3
export const USER_PROFILE_MAIN_AUDIT_FAILED = 4

// 认证头部配置
const authHeader = {
  Authorization: 'Basic ' + Base64.btoa('webApp:webApp'),
}

// 用户API接口
export const userApi = {
  // 手机号验证码登录
  mobileLogin: (data: MobileLoginParams): Promise<V1ApiResponse<MobileLoginData>> =>
    api.post(
      '/v1/users/mobile-login',
      {
        authType: data.authType ?? AUTH_TYPE_MOBILE,
        mobile: data.mobile,
        code: data.code,
        smsPushCode: data.smsPushCode || APP_LOGIN_SMS_CODE,
        ...(data.invitationCode ? { invitationCode: data.invitationCode } : {}),
      },
      { noAuthRedirect: true, skipAuthRefresh: true } as any
    ),

  // 刷新登录令牌
  refreshToken: (refreshToken: string): Promise<V1ApiResponse<RefreshTokenData>> =>
    api.post(
      '/v1/users/refresh-token',
      { refreshToken },
      { noAuthRedirect: true, skipAuthRefresh: true } as any
    ),

  /** 查询当前用户信息，无需传递查询参数 */
  getUserDetail: (): Promise<V1ApiResponse<UserDetailData>> => api.get('/v1/users/detail'),

  /** 修改当前用户资料（头像 / 昵称 / 简介等），服务端按差异审核 */
  updateProfile: (data: UpdateUserProfileParams): Promise<V1ApiResponse<UpdateUserProfileData>> =>
    api.patch('/v1/users/me/profile', data),

  // 登录
  login: (data: any): Promise<ApiResponse<any>> => {
    // 将数据转换为 URLSearchParams 格式（application/x-www-form-urlencoded）
    const formData = new URLSearchParams()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    return api.post('/api-uaa/oauth/token', formData, {
      headers: {
        ...authHeader,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
  },

  /** 退出登录，撤销当前会话的 access token 和 refresh token */
  logout: (): Promise<V1ApiResponse<Record<string, never>>> =>
    api.post('/v1/users/logout', undefined, { noAuthRedirect: true } as any),

  // 获取验证码
  getSmsCode: (mobile: string): Promise<ApiResponse> =>
    api.get(`/api-uaa/validata/smsCode/${mobile}`),

  // 获取用户信息
  getUserInfo: (mobile: string): Promise<ApiResponse> => api.get(`/api-user/userVip/${mobile}`),

  // 更新用户信息
  updateUserInfo: (data: any): Promise<ApiResponse> => api.post('/api-user/userVip/update', data),

  // 用户点赞创意模版
  userLikes: (data: any): Promise<ApiResponse> =>
    api.post('/api-algorithm/userLikes/like', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    }),

  // 用户点赞记录
  getUserLikesPage: (params: any): Promise<ApiResponse> =>
    api.get('/api-algorithm/userLikes/page', { params }),

  // 用户点赞记录的详情
  getUserLikeDetails: (params: any): Promise<ApiResponse> =>
    api.get('/api-algorithm/userLikes/details', { params }),

  // 每月登录奖励领取
  monthlyReward: (params: { userId?: string; mobile?: string }): Promise<ApiResponse> =>
    api.get('/api-user/monthly-login-reward/handle', { params }),

  // 用户收藏/取消收藏
  // collectType类型: 1模特姿势库 2商品库 3穿戴参考库 4模特库 5模特场景库 6上装库 7下装库 8鞋靴库
  // 详见 @/constants/collectType
  userCollect: (data: any): Promise<ApiResponse> =>
    api.post('/api-algorithm/user-collect/collect', data),

  /** 发起当前用户账户注销，冷静期由服务端配置决定 */
  initiateAccountCancellation: (): Promise<V1ApiResponse<AccountCancellationData>> =>
    api.post('/v1/users/me/account-cancellation'),

  /** 查询当前用户最近一轮注销流程；从未发起时 data 为 null */
  getAccountCancellation: (): Promise<V1ApiResponse<AccountCancellationData | null>> =>
    api.get('/v1/users/me/account-cancellation'),

  /** 终止当前用户待执行的账户注销流程 */
  terminateAccountCancellation: (): Promise<V1ApiResponse<AccountCancellationData>> =>
    api.post('/v1/users/me/account-cancellation/terminate'),

  // 核验用户邀请码
  checkInvitationsCode: (params: any): Promise<ApiResponse> =>
    api.get('/api-user/user/checkInvitationsCode', { params }),

  // 获取用户邀请码信息
  getUserInvitationsCode: (params: any): Promise<ApiResponse> =>
    api.get('/api-user/user/getUserInvitationsCode', { params }),

  /**
   * 获取产品介绍（使用教程 / 飞书文档链接）
   * productCategoriesCode:
   *  - ALL: APP 产品使用说明书（默认）
   *  - PC_ALL: PC 整体功能产品使用说明书
   */
  findProductIntroduction: (params: {
    productCategoriesCode?: 'ALL' | 'PC_ALL'
  }): Promise<ApiResponse> => api.get('/api-user/app/findProductIntroduction', { params }),
}

export default userApi
